<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import type { UploadFile } from 'element-plus'

import { getSiteByID } from '@/services/core.service'
import { useProgramSuccess } from '@/composables/cpm/programSuccess'
import type { IProgramAgreement } from '@/types/cpm/programSuccess'
import { useOIDCStore } from '@/stores/oidc.store'
import RlUploadPdf from '@/components/common/upload/RlUploadPdf.vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { Icon } from '@iconify/vue'

/**
 * Site header metadata plus the CPM program agreement PDF control.
 *
 * @prop siteId    - Selected site id; site details load once it is provided.
 * @prop agreement - Current agreement (surfaced on the program-success payload), or null.
 */
const props = defineProps<{ siteId?: string; agreement: IProgramAgreement | null }>()

const { isAdminUser } = useOIDCStore()

const siteIdRef = computed(() => props.siteId)
const { data: site } = useQuery({
  queryKey: ['core', 'site', siteIdRef],
  queryFn: () => getSiteByID(props.siteId!),
  enabled: computed(() => !!props.siteId),
})

/** Node = elemental node, with the commercial pricing node in parentheses when present. */
const nodeValue = computed(() => {
  const elemental = site.value?.elemental_node
  const commercial = site.value?.commercial_pricing_node
  if (elemental && commercial) return `${elemental} (${commercial})`
  return elemental || commercial || '—'
})

const infoItems = computed(() => [
  { label: 'Node', value: nodeValue.value },
  { label: 'Transmission Operator', value: site.value?.transmission_provider || '—' },
  { label: 'ISO', value: site.value?.iso || '—' },
  { label: 'Transmission Pricing Zone', value: site.value?.transmission_pricing_zone || '—' },
  { label: 'Utility Provider', value: site.value?.power_provider || '—' },
])

// --- CPM agreement upload / delete ---------------------------------------
const { uploadAgreementMutation, deleteAgreementMutation } = useProgramSuccess()

// RlUploadPdf model: an UploadFile after a pick, `null` when cleared, undefined initially.
const agreementFile = ref<UploadFile | null | undefined>()
const agreementUploadRef = ref<InstanceType<typeof RlUploadPdf>>()

const isUploading = computed(() => uploadAgreementMutation.isPending.value)
const isMutating = computed(() => isUploading.value || deleteAgreementMutation.isPending.value)

watch(agreementFile, (file) => {
  if (!props.siteId || !file?.raw || file.raw.type !== 'application/pdf') return
  const { raw, name } = file
  // Release the local preview immediately and upload in the background. The agreement only
  // renders (as a link) once the server confirms it on refetch, so a failed upload leaves the
  // prior state untouched — no optimistic flash to walk back.
  agreementUploadRef.value?.reset()
  uploadAgreementMutation.mutate({ siteId: props.siteId, document: raw, name })
})

function removeAgreement() {
  if (!props.siteId || !props.agreement) return
  DeleteConfirmationModal(
    'Remove Agreement',
    'Are you sure you want to remove the CPM program agreement? This cannot be undone.',
    props.siteId,
    deleteAgreementMutation.mutateAsync
  )
}
</script>

<template>
  <div class="h-full flex flex-col gap-4 overflow-y-auto">
    <!-- Section 1: site header metadata -->
    <div class="grid grid-cols-2 gap-4">
      <div v-for="item in infoItems" :key="item.label" class="flex flex-col gap-1">
        <span class="text-xs text-text-tertiary">{{ item.label }}</span>
        <span class="text-sm">{{ item.value }}</span>
      </div>
    </div>

    <div class="h-px bg-[var(--el-border-color-lighter)]" />

    <!-- Section 2: operations contact -->
    <div class="grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-xs text-text-tertiary">Operations Contact</span>
        <span class="text-sm">{{ site?.management_details?.operator_name || '—' }}</span>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-xs text-text-tertiary">Operations Email</span>
        <a
          v-if="site?.management_details?.operator_email"
          :href="`mailto:${site.management_details.operator_email}`"
          class="text-sm"
          style="color: var(--text-brand)"
        >
          {{ site.management_details.operator_email }}
        </a>
        <span v-else class="text-sm">—</span>
      </div>
    </div>

    <div class="h-px bg-[var(--el-border-color-lighter)]" />

    <!-- Section 3: CPM program agreement -->
    <div class="flex flex-col gap-2">
      <span class="text-xs text-text-tertiary">CPM Program Agreement</span>

      <!-- Presigned S3 URLs can't be previewed cross-origin (no CORS header), so link out instead. -->
      <div v-if="agreement" class="flex items-center justify-between gap-3">
        <a
          :href="agreement.url"
          target="_blank"
          rel="noopener"
          class="text-sm truncate"
          style="color: var(--text-brand)"
        >
          {{ agreement.name }}
        </a>
        <el-button v-if="isAdminUser" link type="danger" :disabled="isMutating" @click="removeAgreement">
          <Icon icon="material-symbols:delete-outline-rounded" inline :height="18" />
        </el-button>
      </div>
      <span v-else-if="!isAdminUser" class="text-sm text-text-tertiary">No agreement uploaded.</span>

      <!-- Uploader shows only when no agreement is set; replace by deleting first, then re-uploading. -->
      <RlUploadPdf
        v-if="isAdminUser && !agreement"
        ref="agreementUploadRef"
        v-model="agreementFile"
        :disabled="isMutating"
      />
      <span v-if="isUploading" class="text-xs text-text-tertiary">Uploading…</span>
    </div>
  </div>
</template>
